// src/modules/sales/hooks/useSaleForm.js
// Satış formu hook - v4 Refactored

import { useState, useEffect } from 'react';
import { doc, getDoc, serverTimestamp } from 'firebase/firestore';
import useCustomerOptions from '../../../hooks/useCustomerOptions';
import { useNavigate } from 'react-router-dom';
import { db } from '../../../firebase';
import { safeTimestamp, createNewSale, updateExistingSale } from './saleFormHelpers';
import { findDuplicateDeviceBySerial, duplicateDeviceMessage } from '../../../utils/deviceDedup';
import { invalidateCollections } from '../../../hooks/useCachedCollection';

export const useSaleForm = (id) => {
    const navigate = useNavigate();
    const isEditMode = Boolean(id);
    const [loading, setLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);

    // Müşteri bilgileri
    // Müşteri listesi: paylaşımlı oturum cache'i (form açılışında tam koleksiyon okuması yok).
    const { customers } = useCustomerOptions();
    const [selectedCustomerId, setSelectedCustomerId] = useState('');

    // Satılan ürünler
    const [satilanUrunler, setSatilanUrunler] = useState([]);

    // Genel bilgiler
    const [toplamTutar, setToplamTutar] = useState(0);
    const [odenenTutar, setOdenenTutar] = useState(0);
    const [odemeTipi, setOdemeTipi] = useState('Nakit');
    const [satisNotlari, setSatisNotlari] = useState('');

    // Cihaz Satışı State
    const [isDeviceSale, setIsDeviceSale] = useState(false);
    const [seriNumarasi, setSeriNumarasi] = useState('');
    const [marka, setMarka] = useState('');
    const [model, setModel] = useState('');
    const [cihazSinifi, setCihazSinifi] = useState('Oksijen Konsantratörü');
    const [isSgk, setIsSgk] = useState(false);
    const [zimmetTarihi, setZimmetTarihi] = useState('');
    const [faturaNo, setFaturaNo] = useState('');
    const [verilisTarihi, setVerilisTarihi] = useState('');
    const [garantiBaslangic, setGarantiBaslangic] = useState('');
    const [garantiBitis, setGarantiBitis] = useState('');
    const [periyodikBakimTarihi, setPeriyodikBakimTarihi] = useState('');


    // Düzenleme modu için verileri çek
    useEffect(() => {
        if (isEditMode) {
            setLoading(true);
            const fetchSaleRecord = async () => {
                try {
                    const docRef = doc(db, "sales", id);
                    const docSnap = await getDoc(docRef);
                    if (docSnap.exists()) {
                        const data = docSnap.data();
                        setSelectedCustomerId(data.musteriId);
                        setSatilanUrunler(data.satilanUrunler || []);
                        setOdenenTutar(data.odenenTutar || 0);
                        setOdemeTipi(data.odemeTipi || 'Nakit');
                        setSatisNotlari(data.satisNotlari || '');
                    } else {
                        alert("Düzenlenecek satış kaydı bulunamadı!");
                        navigate('/urun-satis-kayitlari');
                    }
                } catch (error) {
                    console.error("Satış kaydı çekilirken hata:", error);
                } finally {
                    setLoading(false);
                }
            };
            fetchSaleRecord();
        } else {
            setLoading(false);
        }
    }, [id, isEditMode, navigate]);

    // Toplam tutarı hesapla
    useEffect(() => {
        const total = satilanUrunler.reduce((sum, p) => sum + (Number(p.toplamFiyat) || 0), 0);
        setToplamTutar(total);
    }, [satilanUrunler]);

    // Handlers
    const handleProductSelect = (itemOrItems) => {
        const itemsArray = Array.isArray(itemOrItems) ? itemOrItems : [itemOrItems];
        const normalizedItems = itemsArray.map(item => ({
            ...item,
            miktar: item.miktar || 1,
            birimFiyat: Number(item.birimFiyat) || Number(item.fiyat) || 0,
            toplamFiyat: (item.miktar || 1) * (Number(item.birimFiyat) || Number(item.fiyat) || 0)
        }));
        setSatilanUrunler([...satilanUrunler, ...normalizedItems]);
    };

    const handleRemoveProduct = (index) => {
        const newData = [...satilanUrunler];
        newData.splice(index, 1);
        setSatilanUrunler(newData);
    };

    const handleMiktarChange = (index, newMiktar) => {
        const newData = [...satilanUrunler];
        const item = newData[index];
        const miktar = Number(newMiktar) || 1;
        const birimFiyat = Number(item.birimFiyat) || 0;
        item.miktar = miktar;
        item.toplamFiyat = miktar * birimFiyat;
        setSatilanUrunler(newData);
    };

    const handleFiyatChange = (index, newFiyat) => {
        const newData = [...satilanUrunler];
        const item = newData[index];
        const birimFiyat = Number(newFiyat) || 0;
        const miktar = Number(item.miktar) || 1;
        item.birimFiyat = birimFiyat;
        item.toplamFiyat = miktar * birimFiyat;
        setSatilanUrunler(newData);
    };

    const handleSubmit = async () => {
        if (!selectedCustomerId) {
            alert("Lütfen Müşteri seçimi yapınız.");
            return;
        }
        if (satilanUrunler.length === 0) {
            alert("Lütfen en az bir ürün ekleyiniz.");
            return;
        }

        if (isDeviceSale) {
            if (!seriNumarasi || !marka) {
                alert("Cihaz kaydı oluşturulacağı için Seri Numarası ve Marka alanları zorunludur.");
                return;
            }
        }

        const customer = customers.find(c => c.id === selectedCustomerId);

        const salesData = {
            musteriId: customer.id,
            musteriAdSoyad: customer.adSoyad,
            musteriTelefon: customer.telefon || '',
            satilanUrunler,
            toplamTutar,
            odenenTutar: Number(odenenTutar),
            kalanTutar: toplamTutar - Number(odenenTutar),
            odemeTipi,
            satisNotlari,
            durum: "Tamamlandı",
            olusturmaTarihi: serverTimestamp()
        };

        try {
            setIsSaving(true);
            if (isEditMode) {
                await updateExistingSale(id, salesData, customer, toplamTutar, odenenTutar, odemeTipi);
                // Yazma birden çok koleksiyonu etkiler; açık listeler (satışlar, cari,
                // müşteriler) anında güncellensin diye cache'leri geçersiz kıl.
                await invalidateCollections(['sales', 'transactions', 'customers']);
                alert("Satış kaydı ve finansal veriler başarıyla güncellendi.");
                navigate('/urun-satis-kayitlari');
            } else {
                const deviceData = isDeviceSale ? {
                    musteriId: customer.id,
                    musteriAdSoyad: customer.adSoyad,
                    seriNumarasi: seriNumarasi.trim(),
                    marka,
                    model,
                    cihazSinifi,
                    isSgk,
                    zimmetTarihi: isSgk ? safeTimestamp(zimmetTarihi) : null,
                    faturaNo,
                    verilisTarihi: safeTimestamp(verilisTarihi),
                    garantiBaslangic: safeTimestamp(garantiBaslangic),
                    garantiBitis: safeTimestamp(garantiBitis),
                    periyodikBakimTarihi: safeTimestamp(periyodikBakimTarihi),
                } : null;

                // Mükerrer cihaz denetimi: aynı seri no zaten kayıtlıysa satışla
                // birlikte ikinci bir cihaz kaydı AÇILMAZ; kullanıcı uyarılır ve abone olunan
                // cihaz mevcut kayıttan kullanılır.
                if (isDeviceSale && deviceData) {
                    const duplicate = await findDuplicateDeviceBySerial(deviceData.seriNumarasi);
                    if (duplicate) {
                        alert(duplicateDeviceMessage(duplicate, deviceData.seriNumarasi));
                        return;
                    }
                }

                await createNewSale(salesData, customer, satilanUrunler, toplamTutar, odenenTutar, odemeTipi, isDeviceSale, deviceData);
                // Satış oluşturma; sales, transactions, customers (bakiye), stocks (stok
                // düşümü) ve gerekiyorsa devices koleksiyonlarını yazar. Açık listeler
                // eski cache'i göstermesin diye hepsini geçersiz kıl (bu yapılmadığı için
                // "satışı tamamla çalışmıyor, sayfayı yenileyip baştan giriyorum" ve
                // mükerrer kayıt sorunları oluşuyordu).
                await invalidateCollections(['sales', 'transactions', 'customers', 'stocks', 'devices']);
                alert("Satış başarıyla oluşturuldu" + (isDeviceSale ? " ve cihaz kaydı açıldı." : "!"));
                navigate('/urun-satis-kayitlari');
            }
        } catch (e) {
            console.error("Satış kaydedilirken hata oluştu: ", e);
            alert(`Bir hata oluştu: ${e.message}`);
        } finally {
            setIsSaving(false);
        }
    };

    return {
        isEditMode,
        loading,
        isSaving,
        customers,
        selectedCustomerId,
        setSelectedCustomerId,
        satilanUrunler,
        toplamTutar,
        odenenTutar,
        setOdenenTutar,
        odemeTipi,
        setOdemeTipi,
        satisNotlari,
        setSatisNotlari,
        handleProductSelect,
        handleRemoveProduct,
        handleMiktarChange,
        handleFiyatChange,
        handleSubmit,
        // Cihaz Props
        isDeviceSale, setIsDeviceSale,
        seriNumarasi, setSeriNumarasi,
        marka, setMarka,
        model, setModel,
        cihazSinifi, setCihazSinifi,
        isSgk, setIsSgk,
        zimmetTarihi, setZimmetTarihi,
        faturaNo, setFaturaNo,
        verilisTarihi, setVerilisTarihi,
        garantiBaslangic, setGarantiBaslangic,
        garantiBitis, setGarantiBitis,
        periyodikBakimTarihi, setPeriyodikBakimTarihi
    };
};
